import { useTheme } from '@mui/material/styles';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import SpeedGauge from '@components/Gauge';
import { Download, Upload, Gauge } from 'lucide-react';
import {
  MainContainer,
  ToolHeaderRow,
  ToolTitleGroup,
  ToolTitleText,
  ToolContentBox,
  CardHeaderRow,
  CardTitleGroup,
  CardTitleText,
  TwoCardsRow,
  CardColumn,
  SpeedCard,
  HeaderIconBox,
  GaugeCenteredBox,
  GaugeAccentLine,
  PhaseIndicator,
  LoadingOverlay,
  LoadingText,
} from './styles';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import {
  DiagnosticsService,
  DSCP_POST_VALUE,
  type DiagnosticParam,
  type DiagnosticParamPost,
} from '@/services/diagnosticsService';
import {
  DiagnosticAbortError,
  DiagnosticTimeoutError,
  DiagnosticTriggerError,
  type SpeedSseResult,
} from '@/services/diagnosticsSse';
import { useDiagnosticRunner } from '@/hooks/useDiagnosticRunner';
import { enqueueSnackbar } from 'notistack';
import { env } from '@/config/env';

export type TestPhase =
  | 'idle'
  | 'loading-download'
  | 'download'
  | 'loading-upload'
  | 'upload'
  | 'done';

interface TestResults {
  download: number;
  upload: number;
}

export interface SpeedRef {
  startTest: () => void;
  resetTest: () => void;
}

interface SpeedProps {
  onStateChange?: (state: { phase: TestPhase; isRunning: boolean }) => void;
  equipmentId: string;
  server?: SpeedServer;
}

export type SpeedServer = '1' | '2';

// Runtime-configured endpoints; unset or invalid entries are empty strings.
const SERVERS = env.speedtestServers;

// Simulates a realistic speed oscillation pattern,
function simulateSpeedValue(
  progress: number,
  targetSpeed: number,
  seed: number
): number {
  if (progress < 0.15) {
    const rampFactor = progress / 0.65;
    const eased = rampFactor * rampFactor;
    return targetSpeed * eased * (0.6 + 0.4 * Math.sin(seed + progress * 30));
  }
  if (progress < 0.8) {
    const oscillation =
      Math.sin(progress * 25 + seed) * 0.15 +
      Math.sin(progress * 47 + seed * 2) * 0.08 +
      Math.sin(progress * 13 + seed * 3) * 0.05;
    return targetSpeed * (0.85 + oscillation);
  }
  const convergeFactor = (progress - 0.8) / 0.2;
  const remainingOscillation =
    Math.sin(progress * 25 + seed) * 0.05 * (1 - convergeFactor);
  return targetSpeed * (1 + remainingOscillation);
}

// TR-069 Download/Upload Diagnostics input param name suffixes.
const SPEED_INPUT_SUFFIXES = [
  'DownloadURL',
  'UploadURL',
  'DSCP',
  'EthernetPriority',
  'TestFileLength',
];

function isSpeedInput(param: DiagnosticParam): boolean {
  return SPEED_INPUT_SUFFIXES.some((suffix) => param.name?.endsWith(suffix));
}

/** Fixed upload file size */
const TEST_FILE_LENGTH_BYTES = String(100 * 1024 * 1024);

// Builds the POST body for a speed diagnostic.
function buildSpeedBody(
  params: DiagnosticParam[],
  url: string,
  stateParamId: number | null,
  dscpParamId: number | null
): DiagnosticParamPost[] {
  const stateParam: DiagnosticParamPost | null = stateParamId
    ? { id: stateParamId, value: 'Requested' }
    : null;

  if (params.length === 0) {
    return [...(stateParam ? [stateParam] : []), { id: 0, value: url }];
  }

  const inputParams = params.filter(isSpeedInput);

  const urlParam = inputParams.find(
    (p) =>
      p.name?.endsWith('DownloadURL') ||
      p.name?.endsWith('UploadURL') ||
      (typeof p.value === 'string' && p.value.startsWith('http'))
  );

  return [
    ...(stateParam ? [stateParam] : []),
    ...inputParams.map(
      (p): DiagnosticParamPost =>
        p.id === dscpParamId
          ? { id: p.id, value: DSCP_POST_VALUE }
          : p.id === urlParam?.id
            ? { id: p.id, value: url }
            : p.name?.endsWith('TestFileLength')
              ? { id: p.id, value: TEST_FILE_LENGTH_BYTES }
              : { id: p.id, value: p.value ?? '' }
    ),
  ];
}

const Speed = forwardRef<SpeedRef, SpeedProps>(
  ({ onStateChange, equipmentId, server = '1' }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const [phase, setPhase] = useState<TestPhase>('idle');
    const [downloadGaugeValue, setDownloadGaugeValue] = useState(0);
    const [uploadGaugeValue, setUploadGaugeValue] = useState(0);
    const [results, setResults] = useState<TestResults>({
      download: 0,
      upload: 0,
    });

    const animFrameRef = useRef<number>(0);
    const phaseStartRef = useRef<number>(0);
    const seedRef = useRef<number>(0);
    const { run, cancel } = useDiagnosticRunner();

    const resetTest = useCallback(() => {
      cancel();
      cancelAnimationFrame(animFrameRef.current);
      setPhase('idle');
      setDownloadGaugeValue(0);
      setUploadGaugeValue(0);
      setResults({ download: 0, upload: 0 });
    }, [cancel]);

    const runPhase = useCallback(
      (
        targetSpeed: number,
        duration: number,
        setGaugeValue: React.Dispatch<React.SetStateAction<number>>,
        onComplete: (finalSpeed: number) => void
      ) => {
        phaseStartRef.current = performance.now();
        seedRef.current = Math.random() * 100;

        const animate = (now: number) => {
          const elapsed = now - phaseStartRef.current;
          const progress = Math.min(elapsed / duration, 1);
          const speed = simulateSpeedValue(
            progress,
            targetSpeed,
            seedRef.current
          );

          setGaugeValue(Math.max(0, speed));

          if (progress < 1) {
            animFrameRef.current = requestAnimationFrame(animate);
          } else {
            const finalSpeed = targetSpeed * (0.97 + Math.random() * 0.06);
            setGaugeValue(finalSpeed);
            onComplete(finalSpeed);
          }
        };

        animFrameRef.current = requestAnimationFrame(animate);
      },
      []
    );

    const startTest = useCallback(async () => {
      if (!equipmentId) {
        enqueueSnackbar(t('tools.errors.invalidEquipmentOrInterface'), {
          variant: 'error',
        });
        return;
      }

      // Don't send the CPE an empty DownloadURL.
      if (!SERVERS[server].download || !SERVERS[server].upload) {
        enqueueSnackbar(t('tools.errors.speedServerNotConfigured'), {
          variant: 'error',
        });
        return;
      }

      setResults({ download: 0, upload: 0 });
      setDownloadGaugeValue(0);
      setUploadGaugeValue(0);
      setPhase('loading-download');

      try {
        // GET params from service to build the POST body for download diagnostics
        const {
          params: dlParams,
          stateParamId: dlStateId,
          dscpParamId: dlDscpId,
        } = await DiagnosticsService.getParams(equipmentId, 'download');
        const dlBody = buildSpeedBody(
          dlParams,
          SERVERS[server].download,
          dlStateId,
          dlDscpId
        );
        // Opens the download SSE stream, POSTs the trigger (202) and resolves
        // with the DiagnosticResult event sent before the stream closes.
        const { result: dlRes } = await run<SpeedSseResult>(
          equipmentId,
          'download',
          dlBody
        );

        const dlMbps = Number(dlRes.speedMbps) || 0;

        // A zero or missing speed means the device reported a failed measurement.
        if (dlMbps === 0) {
          enqueueSnackbar(t('tools.errors.invalidSpeedResult'), {
            variant: 'error',
          });
          setPhase('done');
          return;
        }

        setPhase('download');

        runPhase(dlMbps, 3000, setDownloadGaugeValue, async () => {
          const dlResult = parseFloat(dlMbps.toFixed(2));
          setResults((prev) => ({ ...prev, download: dlResult }));
          setDownloadGaugeValue(dlResult);

          setPhase('loading-upload');

          try {
            const {
              params: ulParams,
              stateParamId: ulStateId,
              dscpParamId: ulDscpId,
            } = await DiagnosticsService.getParams(equipmentId, 'upload');
            const ulBody = buildSpeedBody(
              ulParams,
              SERVERS[server].upload,
              ulStateId,
              ulDscpId
            );
            // Opens the upload SSE stream, only after the download run finished.
            const { result: ulRes } = await run<SpeedSseResult>(
              equipmentId,
              'upload',
              ulBody
            );

            const ulMbps = Number(ulRes.speedMbps) || 0;

            if (ulMbps === 0) {
              enqueueSnackbar(t('tools.errors.invalidSpeedResult'), {
                variant: 'error',
              });
              setPhase('done');
              return;
            }

            setPhase('upload');

            runPhase(ulMbps, 3000, setUploadGaugeValue, () => {
              const ulResult = parseFloat(ulMbps.toFixed(2));
              setResults((prev) => ({ ...prev, upload: ulResult }));
              setUploadGaugeValue(ulResult);
              setPhase('done');
            });
          } catch (error) {
            // A cancelled run already had its UI state restored by resetTest.
            if (error instanceof DiagnosticAbortError) return;

            enqueueSnackbar(
              error instanceof DiagnosticTimeoutError
                ? t('tools.errors.diagnosticTimeout')
                : error instanceof DiagnosticTriggerError
                  ? t('tools.errors.diagnosticTriggerFailed')
                  : t('tools.errors.uploadFailed'),
              { variant: 'error' }
            );
            setPhase('done');
          }
        });
      } catch (error) {
        if (error instanceof DiagnosticAbortError) return;

        enqueueSnackbar(
          error instanceof DiagnosticTimeoutError
            ? t('tools.errors.diagnosticTimeout')
            : error instanceof DiagnosticTriggerError
              ? t('tools.errors.diagnosticTriggerFailed')
              : t('tools.errors.downloadFailed'),
          { variant: 'error' }
        );
        setPhase('done');
      }
    }, [equipmentId, run, runPhase, server, t]);

    useImperativeHandle(ref, () => ({
      startTest,
      resetTest,
    }));

    useEffect(() => {
      return () => cancelAnimationFrame(animFrameRef.current);
    }, []);

    const isRunning = phase !== 'idle' && phase !== 'done';

    useEffect(() => {
      if (onStateChange) {
        onStateChange({ phase, isRunning });
      }
    }, [phase, isRunning, onStateChange]);

    const isDownloadActive = phase === 'download';
    const isUploadActive = phase === 'upload';
    const isDownloadDone = results.download > 0;
    const isUploadDone = results.upload > 0;

    return (
      <MainContainer>
        <ToolHeaderRow>
          <ToolTitleGroup>
            <Gauge color={theme.palette.white['06']} size={22} />
            <ToolTitleText variant="h3">
              {t('equipment.speed.title')}
            </ToolTitleText>
          </ToolTitleGroup>
        </ToolHeaderRow>

        <ToolContentBox>
          <TwoCardsRow>
            {/* Download Card */}
            <CardColumn>
              <SpeedCard>
                {phase === 'loading-download' && (
                  <LoadingOverlay>
                    <CircularProgress
                      enableTrackSlot
                      size="20px"
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <LoadingText variant="body2">
                      {t('equipment.latency.running')} (Download)
                    </LoadingText>
                  </LoadingOverlay>
                )}

                <CardHeaderRow>
                  <CardTitleGroup>
                    <HeaderIconBox $type="download">
                      <Download size={18} />
                    </HeaderIconBox>
                    <CardTitleText variant="h4">
                      {t('equipment.speed.download')}
                    </CardTitleText>
                  </CardTitleGroup>

                  {isDownloadActive && (
                    <PhaseIndicator $type="download">
                      {t('equipment.speed.downloading')}
                    </PhaseIndicator>
                  )}
                </CardHeaderRow>

                <GaugeCenteredBox>
                  <SpeedGauge
                    value={downloadGaugeValue}
                    size={220}
                    label="download"
                  />
                  <GaugeAccentLine $active={isDownloadDone} $type="download" />
                </GaugeCenteredBox>
              </SpeedCard>
            </CardColumn>

            {/* Upload Card */}
            <CardColumn>
              <SpeedCard>
                {phase === 'loading-upload' && (
                  <LoadingOverlay>
                    <CircularProgress
                      enableTrackSlot
                      size="20px"
                      sx={{
                        color:
                          theme.palette.secondary['02'] ??
                          theme.palette.warning.main,
                      }}
                    />
                    <LoadingText variant="body2">
                      {t('equipment.latency.running')} (Upload)
                    </LoadingText>
                  </LoadingOverlay>
                )}

                <CardHeaderRow>
                  <CardTitleGroup>
                    <HeaderIconBox $type="upload">
                      <Upload size={18} />
                    </HeaderIconBox>
                    <CardTitleText variant="h4">
                      {t('equipment.speed.upload')}
                    </CardTitleText>
                  </CardTitleGroup>

                  {isUploadActive && (
                    <PhaseIndicator $type="upload">
                      {t('equipment.speed.uploading')}
                    </PhaseIndicator>
                  )}
                </CardHeaderRow>

                <GaugeCenteredBox>
                  <SpeedGauge
                    value={uploadGaugeValue}
                    size={220}
                    label="upload"
                  />
                  <GaugeAccentLine $active={isUploadDone} $type="upload" />
                </GaugeCenteredBox>
              </SpeedCard>
            </CardColumn>
          </TwoCardsRow>
        </ToolContentBox>
      </MainContainer>
    );
  }
);

export default Speed;
